import React from 'react';
import Collection from 'react-virtualized/dist/commonjs/Collection';
import AutoSizer from 'react-virtualized/dist/commonjs/AutoSizer';
import { Dimensions } from 'react-native';
import PropTypes from 'prop-types';
import DefaultCell from './components/DefaultCell';
import Timeline from './components/Timeline';
import Sidebar from './components/Sidebar';
import CurrentTimeLine from './components/CurrentTimeLine';
import { calcMinutes, isToday, isPlayingNow } from '../../utils/DateHelpers';

const { width: initialWidth } = Dimensions.get('window');

export default class EPG extends React.Component {
    constructor(props) {
        super(props);
        this.mouseDown = false;
        this.state = {
            scrollLeft: null,
            scrollTop: 0,
            isEndReached: false,
            isScrolling: false
        };
    }

    async componentDidMount() {
        const { currentTime, theme } = this.props;
        this.setState({
            scrollLeft: Math.max(
                0,
                calcMinutes(currentTime) * theme.PIXELS_PER_MIN -
                    (initialWidth - theme.sidebar.container.width) / 2
            )
        });
    }

    componentDidUpdate(prevProps) {
        const { idOfSelectedChannel } = this.props;

        if (prevProps.idOfSelectedChannel !== idOfSelectedChannel) {
            this.setState({
                scrollTop: idOfSelectedChannel * this.props.theme.cell.container.height || 0
            });
        }
        if (prevProps.data.length !== this.props.data.length) {
            this.setState({
                isEndReached: false
            });
        }
    }

    scrollTop = () => {
        this.setState({ scrollTop: 0 });
    };

    cellRenderer = ({ index, key, style }) => {
        const {
            data,
            channels,
            theme,
            onPressCell,
            currentTime,
            lockedContentTitle,
            is12HourClock,
            lastDateChange
        } = this.props;

        return index === data.length ? (
            <CurrentTimeLine
                key={key}
                timelinePosition={
                    calcMinutes(currentTime) * theme.PIXELS_PER_MIN + theme.sidebar.container.width
                }
                height={channels.length * theme.cell.container.height}
                theme={theme.currentTime.line}
            />
        ) : this.props.customCell ? (
            this.props.customCell(
                data[index],
                isPlayingNow(Number(currentTime), data[index].getStart(), data[index].getEnd()),
                style,
                (el) => (this[data[index].getSelector()] = el)
            )
        ) : (
            <DefaultCell
                data={data[index]}
                key={key}
                style={style}
                isPlayingNow={isPlayingNow(
                    Number(currentTime),
                    data[index].getStart(),
                    data[index].getEnd()
                )}
                forceUpdate={lastDateChange}
                theme={theme.cell}
                lockedContentTitle={lockedContentTitle}
                innerRef={(el) => (this[data[index].getSelector()] = el)}
                onPressCell={this.state.isScrolling ? () => {} : onPressCell}
                is12HourClock={is12HourClock}
            />
        );
    };

    cellSizeAndPositionGetter = ({ index }) => {
        const { theme, data, channels, currentTime } = this.props;
        const datum = data[index];
        return index === data.length
            ? {
                  height: channels.length * theme.cell.container.height,
                  width: theme.currentTime.line.width,
                  x:
                      calcMinutes(currentTime) * theme.PIXELS_PER_MIN +
                      theme.sidebar.container.width,
                  y: 0
              }
            : {
                  height: datum.getHeight(),
                  width: datum.getWidth(),
                  x: datum.getX() + theme.sidebar.container.width,
                  y: datum.getY()
              };
    };

    onScroll = ({ scrollLeft, scrollTop }, height) => {
        const { onVerticalEndReached, channels, theme } = this.props;

        this.setState({ scrollLeft, scrollTop });
        if (scrollTop + height >= theme.cell.container.height * (channels.length - 1)) {
            onVerticalEndReached();
            this.setState({
                isEndReached: true
            });
        } else {
            this.setState({
                isEndReached: false
            });
        }
    };

    onMouseMove = (e, height, width) => {
        const { theme, channels } = this.props;
        if (this.mouseDown) {
            e.persist();
            this.setState({ isScrolling: true });
            const scrollLeft =
                this.state.scrollLeft + (this.lastClientX - (this.lastClientX = e.clientX));

            const scrollTop =
                this.state.scrollTop + (this.lastClientY - (this.lastClientY = e.clientY));

            const maxScrollLeft =
                theme.PIXELS_PER_MIN * 24 * 60 - (width - theme.sidebar.container.width);
            const maxScrollTop =
                theme.cell.container.height * channels.length -
                (height - theme.timeline.container.height);
            this.setState({
                scrollLeft:
                    scrollLeft < 0 ? 0 : scrollLeft > maxScrollLeft ? maxScrollLeft : scrollLeft,
                scrollTop: scrollTop < 0 ? 0 : scrollTop > maxScrollTop ? maxScrollTop : scrollTop
            });
        }
    };

    onMouseUp = () => {
        this.mouseDown = false;
        this.setState({ isScrolling: false });
    };

    onMouseDown = (e) => {
        e.preventDefault();
        if (!this.mouseDown) {
            this.mouseDown = true;
            this.lastClientX = e.clientX;
            this.lastClientY = e.clientY;
        }
    };

    render() {
        const { scrollLeft, scrollTop, isEndReached } = this.state;
        const {
            channels,
            collectionRef,
            currentTime,
            data,
            selectedDate,
            timelineRef,
            theme,
            // loadingIndicator,
            isFetchingDone,
            currentTimeLabelText,
            is12HourClock,
            idOfSelectedChannel
        } = this.props;
        return (
            <div
                style={{
                    flex: 1,
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column'
                }}
            >
                <AutoSizer>
                    {({ width, height }) => (
                        <>
                            <Timeline
                                scrollLeft={scrollLeft}
                                width={width - theme.sidebar.container.width}
                                theme={theme}
                                currentTime={currentTime}
                                showCurrentTime={isToday(currentTime, selectedDate)}
                                timeLabelPosition={calcMinutes(currentTime) * theme.PIXELS_PER_MIN}
                                timelineRef={timelineRef}
                                currentTimeLabelText={currentTimeLabelText}
                                is12HourClock={is12HourClock}
                            />
                            <div
                                onMouseDown={this.onMouseDown}
                                onMouseUp={this.onMouseUp}
                                onMouseMove={(e) => this.onMouseMove(e, height, width)}
                                style={{
                                    flex: 1,
                                    display: 'flex',
                                    flexDirection: 'row',
                                    width
                                }}
                            >
                                <Sidebar
                                    scrollTop={scrollTop}
                                    channels={channels}
                                    height={height - theme.timeline.container.height}
                                    theme={theme}
                                />
                                <Collection
                                    style={{
                                        overflow: 'overlay',
                                        position: 'absolute',
                                        overscrollBehavior: 'none'
                                    }}
                                    cellCount={
                                        isToday(currentTime, selectedDate)
                                            ? data.length + 1
                                            : data.length
                                    }
                                    cellRenderer={this.cellRenderer}
                                    cellSizeAndPositionGetter={this.cellSizeAndPositionGetter}
                                    height={height - theme.timeline.container.height}
                                    width={width}
                                    onScroll={(e) => this.onScroll(e, height)}
                                    scrollLeft={scrollLeft}
                                    scrollTop={scrollTop}
                                    verticalOverscanSize={0}
                                    horizontalOverscanSize={0}
                                    ref={collectionRef}
                                />
                            </div>
                        </>
                    )}
                </AutoSizer>
                {isEndReached && !isFetchingDone && (
                    <div
                        style={{
                            position: 'absolute',
                            bottom: 0,
                            left: 0,
                            right: 0,
                            height: 50
                        }}
                    >
                        {/* {loadingIndicator()} */}
                    </div>
                )}
            </div>
        );
    }
}

EPG.propTypes = {
    channels: PropTypes.array,
    currentTime: PropTypes.instanceOf(Date),
    customCell: PropTypes.func,
    data: PropTypes.array,
    onPressCell: PropTypes.func,
    selectedDate: PropTypes.instanceOf(Date),
    theme: PropTypes.object
};

EPG.defaultProps = {
    channels: [],
    currentTime: new Date(),
    customCell: null,
    data: [],
    onPressCell: () => {},
    selectedDate: new Date(),
    theme: {}
};
